import { useCallback, useEffect, useMemo, useState } from "react";
import {
    API,
    deleteControleDossierLot,
    deleteControleDossierLotAttachment,
    getControleDossiersLots,
    updateControleDossierLot,
    uploadControleDossierLotAttachment,
} from "../../../../services/api";
import { apiErrorMessage } from "../controleFormat";

function useDossierList(onError) {
    const [ehpads, setEhpads] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadDossiers = useCallback(() => getControleDossiersLots()
        .then(response => setDossiers(response.data || []))
        .catch(error => onError(apiErrorMessage(error, "Impossible de charger les dossiers de lot."))), [onError]);

    useEffect(() => {
        Promise.all([
            API.get("/ehpads").then(response => setEhpads(response.data || [])),
            loadDossiers(),
        ]).finally(() => setLoading(false));
    }, [loadDossiers]);

    return { ehpads, dossiers, setDossiers, loading, loadDossiers };
}

function useActiveDossier({ onError, mergeFormData, defaultFormData, loadDossiers }) {
    const [activeDossier, setActiveDossier] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [saving, setSaving] = useState(false);
    const [attachmentUploading, setAttachmentUploading] = useState(false);

    const showDossier = useCallback(dossier => {
        setActiveDossier(dossier);
        setFormData(mergeFormData(dossier.donnees));
    }, [mergeFormData]);

    const saveDossier = useCallback(async (status, donnees) => {
        if (!activeDossier) return;
        setSaving(true);
        onError("");
        try {
            const response = await updateControleDossierLot(activeDossier.id, { status, donnees });
            showDossier(response.data);
            await loadDossiers();
        } catch (error) {
            onError(apiErrorMessage(error, "Sauvegarde impossible."));
        } finally {
            setSaving(false);
        }
    }, [activeDossier, loadDossiers, onError, showDossier]);

    const changeAttachments = async (change, fallbackError) => {
        setAttachmentUploading(true);
        onError("");
        try {
            showDossier(await change());
            await loadDossiers();
        } catch (error) {
            onError(apiErrorMessage(error, fallbackError));
        } finally {
            setAttachmentUploading(false);
        }
    };

    // afterUpload(dossier, files) peut renvoyer le dossier retouché par l'écran (classement des pièces par section).
    const uploadAttachments = async (files, afterUpload = null) => {
        const selectedFiles = Array.from(files || []);
        if (!activeDossier || selectedFiles.length === 0) return;
        await changeAttachments(async () => {
            let latestDossier = activeDossier;
            for (const file of selectedFiles) {
                latestDossier = (await uploadControleDossierLotAttachment(latestDossier.id, file)).data;
            }
            return afterUpload ? afterUpload(latestDossier, selectedFiles) : latestDossier;
        }, "Import du fichier impossible.");
    };

    const deleteAttachment = async attachmentId => {
        if (!activeDossier || !attachmentId) return;
        await changeAttachments(
            async () => (await deleteControleDossierLotAttachment(activeDossier.id, attachmentId)).data,
            "Suppression du fichier impossible."
        );
    };

    return {
        activeDossier, setActiveDossier, formData, setFormData, saving, setSaving, attachmentUploading,
        showDossier, saveDossier, uploadAttachments, deleteAttachment,
    };
}

function useDossierDeletion({ onError, setDossiers, active }) {
    const [contextMenu, setContextMenu] = useState(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);

    useEffect(() => {
        if (!contextMenu) return undefined;
        const close = () => setContextMenu(null);
        const closeOnEscape = event => {
            if (event.key === "Escape") close();
        };
        window.addEventListener("click", close);
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [contextMenu]);

    const openContextMenu = (event, dossier) => {
        event.preventDefault();
        setContextMenu({
            dossier,
            x: Math.min(event.clientX, window.innerWidth - 190),
            y: Math.min(event.clientY, window.innerHeight - 72),
        });
    };

    const requestDelete = dossier => {
        setContextMenu(null);
        setDeleteCandidate(dossier);
    };

    const confirmDelete = async () => {
        if (!deleteCandidate) return;
        active.setSaving(true);
        onError("");
        try {
            await deleteControleDossierLot(deleteCandidate.id);
            setDossiers(previous => previous.filter(dossier => dossier.id !== deleteCandidate.id));
            if (active.activeDossier?.id === deleteCandidate.id) active.setActiveDossier(null);
            setDeleteCandidate(null);
        } catch (error) {
            onError(apiErrorMessage(error, "Suppression du dossier impossible."));
        } finally {
            active.setSaving(false);
        }
    };

    return {
        contextMenu, openContextMenu, closeContextMenu: () => setContextMenu(null),
        deleteCandidate, requestDelete, cancelDelete: () => setDeleteCandidate(null), confirmDelete,
    };
}

// Dossiers de lot d'un écran de contrôle : liste, dossier ouvert, enregistrement, pièces jointes et suppression.
export function useControleDossiers({ onError, mergeFormData, defaultFormData }) {
    const list = useDossierList(onError);
    const active = useActiveDossier({ onError, mergeFormData, defaultFormData, loadDossiers: list.loadDossiers });
    const deletion = useDossierDeletion({ onError, setDossiers: list.setDossiers, active });
    const [selectedEhpadId, setSelectedEhpadId] = useState("");
    const selectedEhpad = useMemo(
        () => list.ehpads.find(ehpad => String(ehpad.id) === String(selectedEhpadId)),
        [list.ehpads, selectedEhpadId]
    );

    const { showDossier } = active;
    const openDossier = useCallback(dossier => {
        showDossier(dossier);
        setSelectedEhpadId(String(dossier.ehpadId));
        onError("");
    }, [onError, showDossier]);

    return { ...list, ...active, ...deletion, selectedEhpadId, setSelectedEhpadId, selectedEhpad, openDossier };
}

export function useSaveShortcut(enabled, onSave) {
    useEffect(() => {
        const handleShortcut = event => {
            if (!enabled) return;
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                onSave();
            }
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    }, [enabled, onSave]);
}
